import type { ColDef, ColTypeDef, GridOptions, Module } from 'ag-grid-community';
import type { IBlotterRequest, ISearchResponse } from 'apiConnector/blotters';
import type { FilterTranslator } from 'components/shared/Blotter/Grid/Hooks/requestTranslator';
import type { IBlotterItem } from 'models/IBlotterItem';
import type { Observable } from 'rxjs';
import type { Category } from 'shared/middlewares/analytics/logger';
import type { IBlotterColumnDefinition } from 'state/blotters/common/blotter.model';
import type { MakePartial, MakeRequired } from 'types/utils';

export const technicalColumnDefinition: ColTypeDef = {
  resizable: false,
  headerValueGetter: () => '',
  suppressHeaderMenuButton: true,
  suppressColumnsToolPanel: true,
  floatingFilter: false,
  sortable: false,
  pinned: true,
  lockPinned: true,
  lockVisible: true,
};

export interface BlotterGridOwnProps<Item> extends MakeRequired<Partial<GridOptions<Item>>, 'getRowId'> {
  modules?: Module[];
  e2e?: string;
  name: string;
  canSetActive?: (selectedColDef: ColDef | null) => boolean;
  logAnalyticName: Category;
}

export interface BlotterGridStateProps<Item> extends IBlotterPreferenceState {
  columnDefinitions: readonly IBlotterColumnDefinition<Item>[];
  items?: readonly any[];
  page?: number;
}

export interface ColumnPreferenceState {
  colId: string;
  width?: number;
  hide?: boolean;
  pinned: boolean | 'left' | 'right' | null;
  sort?: 'asc' | 'desc' | null;
  filter?: string;
  filterModel?: unknown;
}
export type EditableFieldInfo = {
  status: 'loading' | 'done' | 'failed';
  errors?: string;
  warnings?: string;
};

export interface ColumnGroupPreferenceState {
  groupId: string;
  isExpanded: boolean;
}

export interface IBlotterPreferenceState {
  columnState?: readonly ColumnPreferenceState[];
  columnGroupState?: readonly ColumnGroupPreferenceState[];
  pageSizeState?: number;
}

export interface BlotterGridDispatchProps<T = IBlotterItem> {
  onSetActiveItem?: (item?: T) => void;
  onSelectedItemsChanged?: (selectedItems: readonly T[]) => void;
  onBlotterColumnStateChanged?: (
    columnState: readonly ColumnPreferenceState[],
    newColumnGroups?: readonly ColumnGroupPreferenceState[],
  ) => void;
  onColumnSortChanged?: (sort: string | null, sortDir: 'asc' | 'desc' | null) => void;

  onDisplayItemsCntChanged?: (itemsCnt: number) => void;
  onPageSizeChanged?: (pageSize: number) => void;
}

export type ServerSideBlotterGridProps<
  Request extends IBlotterRequest,
  Item = IBlotterItem,
  Error = undefined,
> = BlotterGridProps<Item> &
  Omit<ServerSideStateProps<Request, Item>, 'blotterRequest' | 'filtersItems'> &
  Omit<ServerSideOwnProps<Request, Item>, 'searchItems' | 'filterTranslators'> &
  Omit<ServerSideDispatchProps<Item, Error>, 'onItemsReceived' | 'onItemsRequested' | 'onRequestFailed'>;

export interface ServerSideStateProps<
  Request extends IBlotterRequest = IBlotterRequest,
  Item = IBlotterItem,
> extends IBlotterPreferenceState {
  blotterRequest: Request;
  columnDefinitions: readonly IBlotterColumnDefinition<Item>[];
  filtersItems?: (items: readonly Item[]) => readonly Item[];
}
export interface ServerSideOwnProps<Request extends IBlotterRequest, Item = IBlotterItem> extends MakePartial<
  BlotterGridOwnProps<Item>,
  'getRowId'
> {
  searchItems: (req: Request) => Observable<ISearchResponse<Item>>;
  filterTranslators?: readonly FilterTranslator[];
  useNativePagination?: boolean;
  maxRowsCount?: number;
  setIsTruncated?: (isTruncated: boolean) => void;
}
export type ServerSideDispatchProps<Item = IBlotterItem, Error = undefined> = {
  onItemsReceived: (response: ISearchResponse<Item>) => void;
  onItemsRequested?: () => void;
  onRequestFailed?: (error: Error) => void;
} & BlotterGridDispatchProps<Item>;

export type BlotterGridProps<Item> = BlotterGridOwnProps<Item> &
  BlotterGridStateProps<Item> &
  BlotterGridDispatchProps<Item>;

export const emptyBlotterPreferences: IBlotterPreferenceState = {
  pageSizeState: 25,
  columnState: [],
};

export type AgEventType = 'columnMoved' | 'columnPinned' | 'columnResized' | 'columnVisible';
